import { useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { useReactToPrint } from "react-to-print";

import Menu from "../components/Menu";
import api from "../services/api";

export default function RelatorioDefeitosRolo() {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef(null);
    const printRef = useRef(null);
    
    const stateDadosRelatorio = location.state?.dadosRelatorio || [];
    const stateInitialDate = location.state?.initialDate || new Date();
    const stateFinalDate = location.state?.finalDate || new Date();

    const [initialDate, setInitialDate] = useState(stateInitialDate);
    const [finalDate, setFinalDate] = useState(stateFinalDate);
    const [nomeRolo, setNomeRolo] = useState('');
    
    const [dadosRelatorio, setDadosRelatorio] = useState(stateDadosRelatorio);
    const [roloAtual, setRoloAtual] = useState([]);
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);

    // Formata data (YYYY-MM-DD) para a API
    const formatarData = (data) => {
        if (!data) return '';
        return data.getFullYear() + '-' + (data.getMonth() + 1) + '-' + data.getDate();
    };

    // Formata números para 2 casas decimais no padrão PT-BR
    const formatarNumero = (valor) => {
        if (!valor || valor === 0) return '';
        return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const triggerPrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Relatorio_Defeitos_${nomeRolo || 'Rolo'}`,
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                setPrinting(true);
                setTimeout(() => resolve(), 600);
            });
        },
        onAfterPrint: () => {
            setPrinting(false);
        }
    });

    const handlePrint = () => {
        if (printRef.current) {
            triggerPrint();
        } else {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: 'Área de impressão não encontrada.' });
        }
    };

    const pesquisarRolo = () => {
        if (!nomeRolo) {
            toast.current.show({ severity: 'warn', summary: 'Aviso', detail: 'Digite um número de rolo para pesquisar.' });
            return;
        }

        setLoading(true);
        const termoBusca = nomeRolo.toString().trim().toLowerCase();

        const filtradoLocal = dadosRelatorio.filter(item => 
            item.nomerolo && item.nomerolo.toString().trim().toLowerCase() === termoBusca
        );

        if (filtradoLocal.length > 0) {
            setRoloAtual(filtradoLocal);
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: `Rolo ${nomeRolo} encontrado.` });
            setLoading(false);
            return;
        }

        const dtInicial = formatarData(initialDate);
        const dtFinal = formatarData(finalDate);

        api.get(`defeitos/historico/${dtInicial}/${dtFinal}`)
            .then((response) => {
                const historicoBanco = response.data.historico || [];
                setDadosRelatorio(historicoBanco); 

                const filtradoBanco = historicoBanco.filter(item => 
                    item.nomerolo && item.nomerolo.toString().trim().toLowerCase() === termoBusca
                );

                if (filtradoBanco.length > 0) {
                    setRoloAtual(filtradoBanco);
                    toast.current.show({ severity: 'success', summary: 'Sucesso', detail: `Rolo ${nomeRolo} carregado do banco.` });
                } else {
                    setRoloAtual([]); 
                    toast.current.show({ severity: 'info', summary: 'Não encontrado', detail: `O rolo ${nomeRolo} não existe neste período.` });
                }
            })
            .catch(() => {
                toast.current.show({ severity: 'error', summary: 'Erro', detail: 'Falha de conexão ao buscar no servidor.' });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const resumo = useMemo(() => {
        if (roloAtual.length === 0) return null;

        const tear = roloAtual[0].tear;
        const defeitosTecelagem = roloAtual.reduce((acc, curr) => {
            const valor = parseFloat(String(curr.metragem).replace(',', '.')) || 0;
            return acc + valor;
        }, 0);

        return {
            tear: tear,
            metrosRolo: 3500, 
            defeitosTecelagem: defeitosTecelagem
        };
    }, [roloAtual]);

    const defeitosAgrupados = useMemo(() => {
        if (roloAtual.length === 0) return {};
        
        const grupos = {};
        
        roloAtual.forEach(item => {
            const defeito = item.defeito || 'Sem Descrição';
            const turma = item.turma || 'Outros'; 
            const metragem = parseFloat(String(item.metragem).replace(',', '.')) || 0;

            if (!grupos[defeito]) {
                grupos[defeito] = { A: 0, B: 0, C: 0, D: 0 }; 
            }
            
            if (grupos[defeito][turma] !== undefined) {
                grupos[defeito][turma] += metragem;
            } else {
                grupos[defeito][turma] = metragem;
            }
        });

        return grupos;
    }, [roloAtual]);

    const tableStyle = { borderCollapse: 'collapse', width: '100%', border: '1px solid #e9ecef', backgroundColor: '#fff' };
    const thStyle = { padding: '0.5rem 1rem', border: '1px solid #e9ecef', color: '#495057', fontWeight: 'bold' };
    const tdStyle = { padding: '0.5rem 1rem', border: '1px solid #e9ecef', color: '#495057' };

    const cardHeader = (
        <div className="p-d-flex p-flex-row p-jc-between p-ai-center p-p-3 p-mb-0" style={{ borderBottom: '1px solid #e0e0e0' }}>
            <h2 className="p-m-0" style={{ color: '#495057' }}>Relatório por Rolo</h2>
            <Button 
                className="p-button-text p-button-secondary p-mt-3 p-mt-md-0" 
                icon="pi pi-arrow-left" 
                label="Voltar ao Histórico" 
                onClick={() => navigate('/defeitos/historico')} 
            />
        </div>
    );

    return (
        <div>
            <Menu />
            <Toast ref={toast} />
            
            <div className="p-m-3">
                <Card header={cardHeader}>
                    <div className="p-d-flex p-flex-column p-mb-5" style={{ gap: '1.5rem', borderBottom: '1px dashed #e9ecef', paddingBottom: '2rem' }}>
                        
                        <div className="p-d-flex p-ai-end p-flex-wrap" style={{ gap: '0.5rem' }}>
                            <div className="p-field p-m-0">
                                <label className="p-d-block p-text-secondary" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Data Inicial</label>
                                <Calendar dateFormat="dd/mm/yy" mask="99/99/9999" value={initialDate} onChange={e => setInitialDate(e.value)} />
                            </div>
                            <div className="p-field p-m-0">
                                <label className="p-d-block p-text-secondary" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Data Final</label>
                                <Calendar dateFormat="dd/mm/yy" mask="99/99/9999" value={finalDate} onChange={e => setFinalDate(e.value)} />
                            </div>
                            
                            <div className="p-field p-m-0 p-ml-3">
                                <label className="p-d-block p-text-secondary" style={{ fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '600' }}>Número do Rolo</label>
                                <div className="p-inputgroup">
                                    <span className="p-inputgroup-addon">
                                        <i className="pi pi-search"></i>
                                    </span>
                                    <InputText
                                        value={nomeRolo}
                                        onChange={(e) => setNomeRolo(e.target.value)}
                                        placeholder="Ex: 26-0502"
                                        onKeyPress={(e) => e.key === 'Enter' && pesquisarRolo()}
                                        style={{ width: '180px' }}
                                    />
                                    <Button 
                                        label="Buscar" 
                                        onClick={pesquisarRolo} 
                                        className="p-button-primary"
                                        icon={loading ? "pi pi-spin pi-spinner" : ""} 
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                        </div>

                        {resumo && (
                            <div>
                                <Button 
                                    icon="pi pi-print" 
                                    className="p-button-secondary p-mr-2" 
                                    onClick={() => handlePrint()} 
                                />
                            </div>
                        )}
                    </div>

                    {resumo && (
                        <div ref={printRef} style={{ padding: printing ? '2rem' : '0' }}>
                            
                            {printing && (
                                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                                    <h2>Relatório de Defeitos - Rolo: {nomeRolo}</h2>
                                    <h3>
                                        {initialDate?.toLocaleDateString('pt-BR')} a {finalDate?.toLocaleDateString('pt-BR')}
                                    </h3>
                                </div>
                            )}

                            <div className={printing ? "" : "p-grid"} style={{ padding: printing ? '0' : '0 1rem', display: printing ? 'block' : 'flex' }}>
                                
                                <div className={printing ? "" : "p-col-12 p-md-5 p-lg-4"} style={{ marginBottom: printing ? '2rem' : '0' }}>
                                    <table style={{ ...tableStyle, maxWidth: printing ? '100%' : '100%' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ ...thStyle, width: '60%' }}>Tear</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{resumo.tear}</td>
                                            </tr>
                                            <tr>
                                                <td style={thStyle}>Metros rolo</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{resumo.metrosRolo}</td>
                                            </tr>
                                            <tr>
                                                <td style={thStyle}>Defeitos tecelagem do rolo</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                    {formatarNumero(resumo.defeitosTecelagem)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className={printing ? "" : "p-col-12 p-md-7 p-lg-8 p-d-flex p-flex-column"} style={{ gap: '1.5rem' }}>
                                    {Object.keys(defeitosAgrupados).map((defeito, index) => {
                                        
                                        const turmas = Object.entries(defeitosAgrupados[defeito]);

                                        return (
                                            <table key={index} style={{ ...tableStyle, maxWidth: printing ? '100%' : '500px', marginBottom: printing ? '1.5rem' : '0' }}>
                                                <tbody>
                                                    {turmas.map(([turma, valor], i) => (
                                                        <tr key={turma}>
                                                            {i === 0 && (
                                                                <td 
                                                                    rowSpan={turmas.length} 
                                                                    style={{ 
                                                                        ...thStyle, 
                                                                        width: '50%', 
                                                                        verticalAlign: 'top', 
                                                                        backgroundColor: '#f8f9fa' 
                                                                    }}
                                                                >
                                                                    {defeito}
                                                                </td>
                                                            )}
                                                            <td style={{ ...tdStyle, width: '25%', textAlign: 'center' }}>
                                                                {turma}
                                                            </td>
                                                            <td style={{ ...tdStyle, width: '25%', textAlign: 'right' }}>
                                                                {formatarNumero(valor)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        );
                                    })}
                                </div>
                                
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}